import {
  ComponentType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  Navigate,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import {
  Container,
  Box,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {
  Hero as HeroBase,
  TickGreenCircleHollowIcon,
} from '../../theme/styled-components';
import ClockIcon from '../../assets/images/clock.svg?react';
import LightbulbIcon from '../../assets/images/lightbulb-icon.svg?react';
import SignIcon from '../../assets/images/sign-icon.svg?react';
import HelpCard from '../../components/action-centre-help-card/container';
import Loading from '../../components/loading/component';
import useProfile from '../../hooks/useProfile';
import Button from '../../ui/Button';
import Form from '../../ui/Form';
import Markdown from '../../ui/Markdown';
import Modal from '../../ui/Modal';
import Panel from '../../ui/Panel';
import Signature from '../../ui/Signature';
import getAssignedApprovalDocAction from '../../actions/approvaldocs/getAssignedApprovalDoc';
import setSignedApprovalDocAction from '../../actions/approvaldocs/setSignedApprovalDoc';
import isDocumentViewerEnabled from '../../services/isDocumentViewerEnabled';

// Cast Hero to accept children properly (styled-components typing issue)
const Hero = HeroBase as ComponentType<{ children?: ReactNode }>;

const useStyles = makeStyles(() => ({
  root: {},
  clockIcon: {
    color: '#FAC81E',
    fill: '#FAC81E',
    width: 42,
  },
}));

interface Partner {
  completedAt?: string;
}

interface Doc {
  ID: string;
  DocName?: string | null;
  DocFileName?: string | null;
  ActionStatus?: string | null;
  Variabilty?: string | null;
  partners?: Partner[];
}

// Accept both internal Doc[] and external GraphQL types (which can have null items)
interface DocsState {
  items?: Array<
    Doc | { ID: string; [key: string]: unknown } | null | undefined
  >;
}

interface SigningState {
  loading?: boolean;
  success?: boolean;
}

interface UnsignedApprovalDocWithFile {
  file?: {
    Blob: Blob;
  } | null;
}

type UnsignedApprovalDocState =
  | UnsignedApprovalDocWithFile
  | Record<string, unknown>
  | null;

function hasFileBlob(
  doc: UnsignedApprovalDocState | undefined,
): doc is { file: { Blob: Blob } } {
  if (doc === null || doc === undefined) return false;
  if (typeof doc !== 'object') return false;
  if (!('file' in doc) || doc.file === null || doc.file === undefined)
    return false;
  if (typeof doc.file !== 'object') return false;
  return 'Blob' in doc.file && doc.file.Blob instanceof Blob;
}

interface DocumentParam {
  DocFileName?: string;
}

interface SigningFormData {
  document?: {
    id: string;
    name: string;
  };
  signature?: boolean;
}

interface ActionSummaryProps {
  /** Whether to show back to actions button */
  showBackToAction?: boolean;
  // Guest mode props (optional - when provided, component uses these instead of internal hooks)
  /** Approval docs from external source (guest mode) */
  approvalDocs?: DocsState;
  /** Unsigned approval doc state from external source (guest mode) */
  unsignedApprovalDoc?: UnsignedApprovalDocState;
  /** Handler to fetch assigned approval doc - guest version takes only document param (guest mode) */
  handleGetAssignedApprovalDoc?: (document: DocumentParam) => void;
  /** Handler to sign the approval doc (guest mode) */
  handleSignedApprovalDocAction?: () => void;
  /** Handler to fetch existing signature (guest mode) */
  handleGetSignature?: () => void;
  /** Handler to save signature data (guest mode) */
  onSignatureData?: (signature: string) => void;
}

export function ActionSummary({
  showBackToAction = true,
  // Guest mode props
  approvalDocs: guestApprovalDocs,
  unsignedApprovalDoc: guestUnsignedApprovalDoc,
  handleGetAssignedApprovalDoc: guestHandleGetAssignedApprovalDoc,
  handleSignedApprovalDocAction: guestHandleSignedApprovalDocAction,
  handleGetSignature: guestHandleGetSignature,
  onSignatureData: guestOnSignatureData,
}: ActionSummaryProps) {
  const classes = useStyles();
  const { guid, resourceId } = useParams<{
    guid?: string;
    resourceId?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const locationState = location.state as { item?: Doc } | null;

  const [adoptSignature, setAdoptSignature] = useState(true);
  const [doc, setDoc] = useState<Doc>();
  const [docLookupDone, setDocLookupDone] = useState(false);
  const [documentSigned, setDocumentSigned] = useState(false);

  // Determine if we're in guest mode based on whether guest props are provided
  const isGuest = guestApprovalDocs !== undefined;

  // Redux selectors for authenticated mode
  // Use simple selectors matching the original container pattern
  const internalApprovalDocs = useSelector(
    (state: { approvaldocs: { get: (key: string) => DocsState } }) =>
      state.approvaldocs.get('docs'),
  );
  const internalUnsignedApprovalDoc = useSelector(
    (state: {
      approvaldocs: { get: (key: string) => UnsignedApprovalDocState };
    }) => state.approvaldocs.get('unsignedApprovalDoc'),
  );
  const internalSigningApprovalDocs = useSelector(
    (state: { approvaldocs: { get: (key: string) => SigningState } }) =>
      state.approvaldocs.get('signingApprovalDocs'),
  );
  const guestSignature = useSelector(
    (state: { signatureData: { getIn: (path: string[]) => string | null } }) =>
      state.signatureData.getIn(['data', 'Signature']),
  );

  // Use external props for guest mode, internal selectors for authenticated mode
  const approvalDocs = isGuest ? guestApprovalDocs : internalApprovalDocs;
  const unsignedApprovalDoc = isGuest
    ? guestUnsignedApprovalDoc
    : internalUnsignedApprovalDoc;
  const signingApprovalDocs = isGuest ? undefined : internalSigningApprovalDocs;

  // Use profile hook for authenticated users, fall back to external props for guests
  const profile = useProfile();
  const contact = profile?.contact;
  const signature = isGuest ? guestSignature ?? undefined : profile?.signature;
  const setSignature = useCallback(
    (value: string) => {
      if (isGuest) {
        guestOnSignatureData?.(value);
        return;
      }
      profile?.setSignature?.(value);
    },
    [isGuest, guestOnSignatureData, profile],
  );
  const targetDocId = guid ?? resourceId ?? locationState?.item?.ID;
  const activeDoc = doc ?? locationState?.item;

  // Fetch signature on mount for guest users
  useEffect(() => {
    if (isGuest && guestHandleGetSignature) {
      guestHandleGetSignature();
    }
  }, [isGuest, guestHandleGetSignature]);

  // Track when document is signed via Redux state
  useEffect(() => {
    if (signingApprovalDocs?.success) {
      setDocumentSigned(true);
    }
  }, [signingApprovalDocs?.success]);

  // Check if already signed on mount
  useEffect(() => {
    if (activeDoc?.ActionStatus === 'Completed') {
      setDocumentSigned(true);
    }
  }, [activeDoc?.ActionStatus]);

  const handleBackToActions = () => {
    navigate('/actions');
  };

  // Internal handlers for authenticated mode
  const internalHandleGetAssignedApprovalDoc = useCallback(
    (ID: string, document: DocumentParam, contactId: string) => {
      if (isDocumentViewerEnabled()) {
        dispatch(
          getAssignedApprovalDocAction(
            ID,
            document as { DocFileName: string },
            contactId,
          ),
        );
      }
    },
    [dispatch],
  );

  const internalHandleSignedApprovalDocAction = useCallback(
    (ID: string) => {
      dispatch(setSignedApprovalDocAction(ID));
    },
    [dispatch],
  );

  // Ref to store the Promise resolver for bridging Redux state to Promise
  const downloadResolverRef = useRef<
    ((value: { file: Blob; id: string; title: string }) => void) | null
  >(null);

  // Resolve the download promise when unsignedApprovalDoc is populated
  useEffect(() => {
    if (hasFileBlob(unsignedApprovalDoc) && downloadResolverRef.current) {
      downloadResolverRef.current({
        file: unsignedApprovalDoc.file.Blob,
        id: activeDoc?.ID ?? '',
        title: activeDoc?.DocName ?? '',
      });
      downloadResolverRef.current = null;
    }
  }, [unsignedApprovalDoc, activeDoc?.ID, activeDoc?.DocName]);

  const downloadDocument = useCallback(
    () =>
      new Promise<{ file: Blob; id: string; title: string }>(
        (resolve, reject) => {
          downloadResolverRef.current = resolve;

          const documentParam: DocumentParam =
            documentSigned && activeDoc?.DocFileName
              ? { DocFileName: activeDoc.DocFileName }
              : {};

          if (isGuest && guestHandleGetAssignedApprovalDoc) {
            guestHandleGetAssignedApprovalDoc(documentParam);
          } else if (contact?.ContactID && activeDoc?.ID) {
            internalHandleGetAssignedApprovalDoc(
              activeDoc.ID,
              documentParam,
              contact.ContactID,
            );
          }

          setTimeout(() => {
            if (downloadResolverRef.current) {
              downloadResolverRef.current = null;
              reject(new Error('Document download timed out'));
            }
          }, 30000);
        },
      ),
    [
      activeDoc,
      contact?.ContactID,
      documentSigned,
      isGuest,
      guestHandleGetAssignedApprovalDoc,
      internalHandleGetAssignedApprovalDoc,
    ],
  );

  const schema = useMemo(
    () => ({
      type: 'object' as const,
      additionalProperties: false,
      properties: {
        document: {
          type: 'object' as const,
          title: activeDoc?.DocName ?? undefined,
          additionalProperties: false,
          properties: {
            id: {
              type: 'string' as const,
            },
            name: {
              type: 'string' as const,
            },
          },
          required: ['id', 'name'],
        },
        signature: {
          type: 'boolean' as const,
          title: 'Client signature',
          description:
            'By signing this document, you agree to the Saltus terms and conditions and confirm that you have read, understood, and agree with the document.',
        },
      },
      required: ['document', 'signature'],
      errorMessage: {
        required: {
          signature: 'Your signature is required',
        },
      },
    }),
    [activeDoc?.DocName],
  );

  const uiSchema = useMemo(
    () => ({
      document: {
        'ui:field': 'document',
        'ui:options': {
          downloadFunction: downloadDocument,
        },
      },
      signature: {
        'ui:widget': 'signature',
      },
      'ui:order': ['document', 'signature'],
    }),
    [downloadDocument],
  );

  const formData = useMemo<SigningFormData>(
    () => ({
      document: activeDoc
        ? {
            id: activeDoc.ID,
            name: activeDoc.DocName ?? '',
          }
        : undefined,
    }),
    [activeDoc],
  );

  const handleSubmit = useCallback(
    ({ formData: data }: { formData?: SigningFormData }) => {
      if (data?.signature && activeDoc?.ID) {
        if (isGuest && guestHandleSignedApprovalDocAction) {
          guestHandleSignedApprovalDocAction();
        } else {
          internalHandleSignedApprovalDocAction(activeDoc.ID);
        }
      }
    },
    [
      activeDoc?.ID,
      isGuest,
      guestHandleSignedApprovalDocAction,
      internalHandleSignedApprovalDocAction,
    ],
  );

  useEffect(() => {
    // Only do lookup when items are available
    if (approvalDocs?.items) {
      const foundDoc = approvalDocs.items.find(
        (item): item is Doc =>
          item !== null && item !== undefined && item.ID === targetDocId,
      );
      setDoc(foundDoc ?? locationState?.item);
      setDocLookupDone(true);
    }
  }, [approvalDocs, targetDocId, locationState?.item]);

  // Show loading while waiting for data or lookup to complete
  if (!activeDoc && !docLookupDone) {
    return <Loading />;
  }

  // Show 404 if lookup completed but document doesn't exist
  if (!activeDoc && docLookupDone) {
    return <Navigate to="/not-found" replace />;
  }

  const { Variabilty, DocName } = activeDoc ?? {};

  return (
    <Grid container className={classes.root}>
      <Container maxWidth="lg">
        <Hero>
          <Box mb={3}>
            <Typography
              color="inherit"
              variant="h1"
              data-testid="action-summary-title"
            >
              {DocName}
            </Typography>
            <br />
          </Box>
        </Hero>

        {Variabilty && !_.isEmpty(Variabilty) && (
          <Box mb={1}>
            <Panel title="Summary" icon={<LightbulbIcon />}>
              <Markdown>{Variabilty}</Markdown>
            </Panel>
          </Box>
        )}

        {documentSigned ? (
          <>
            <Panel centre light>
              {!activeDoc?.partners?.length ||
              activeDoc.partners.every((p) => p.completedAt) ? (
                <>
                  <TickGreenCircleHollowIcon width="30px" height="34px" />

                  <Typography align="center">
                    Thank you for signing the{' '}
                    {activeDoc?.DocName?.toLowerCase()}.
                  </Typography>
                </>
              ) : (
                <>
                  <ClockIcon className={classes.clockIcon} />

                  <Typography align="center">
                    Waiting for other parties to sign
                  </Typography>

                  <Typography align="center">
                    They can complete them using their account
                  </Typography>
                </>
              )}
            </Panel>

            {showBackToAction && (
              <Box mt={1}>
                <Button
                  fullWidth
                  variant="secondary"
                  onClick={handleBackToActions}
                >
                  Back to my actions
                </Button>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box mb={1} data-testid="action-summary-signature-card">
              <Panel title="Review and sign document" icon={<SignIcon />}>
                <Form<SigningFormData>
                  schema={schema}
                  ui={uiSchema}
                  formData={formData}
                  formContext={{
                    setSignature,
                    signature,
                  }}
                  loading={signingApprovalDocs?.loading}
                  onSubmit={handleSubmit}
                />
              </Panel>
            </Box>

            <Box mb={1}>
              <HelpCard />
            </Box>

            {adoptSignature && (
              <Modal
                hideClose
                open={adoptSignature}
                title="Adopt signature"
                closeHandler={() => {}}
              >
                <Box data-testid="action-summary-adopt-signature-modal">
                  <Typography>
                    By selecting Adopt I agree to use the below signature.
                  </Typography>

                  <Signature
                    initialValue={signature}
                    submitLabel="Adopt"
                    onSign={setSignature}
                    onSubmit={() => {
                      setAdoptSignature(false);
                    }}
                  />
                </Box>
              </Modal>
            )}
          </>
        )}
      </Container>
    </Grid>
  );
}

export default ActionSummary;
